import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { mustCompleteOnboarding } from "./onboarding";

/** Caminho do onboarding obrigatório. Literal em um lugar só (INV-04). */
export const ONBOARDING_PATH = "/onboarding";

/**
 * ProtectedRoute — exige token E tenant configurado.
 *
 * A guarda de onboarding mora AQUI, e não num wrapper novo, porque
 * `<ProtectedRoute>` já é o portão único das 27 rotas de produto: um wrapper
 * separado precisaria ser lembrado em cada rota nova, e a rota esquecida não
 * daria erro — daria exatamente a tela vazia que esta guarda existe para
 * eliminar. O custo é acoplar dois conceitos ("está logado" e "terminou o
 * cadastro") no mesmo componente; em troca, é impossível esquecer.
 *
 * ── Três estados, não dois ───────────────────────────────────────────────────
 *
 * 1. **Sem token** → `/login`. Comportamento histórico, inalterado.
 * 2. **`loading`** → não renderiza nada. Antes de o `AuthContext` hidratar,
 *    `user` é `null`, que é indistinguível de "não tem tenant". Sem este ramo,
 *    todo usuário levaria um redirect em falso no primeiro render — inclusive
 *    quem tem tenant há meses. Mesmo padrão do `RoleRoute`.
 * 3. **Sem tenant NENHUM** → `/onboarding`, exceto quando já se está lá (senão
 *    é laço).
 *
 * ⚠️ O critério do ramo 3 é `mustCompleteOnboarding` (não tem `tenant`), NÃO
 * `needsOnboarding` (não tem `tenant.segment`). A diferença não é estilística:
 * o signup por e-mail/senha cria o tenant com `segment` nulo
 * (`authController.ts:168-177`), então bloquear por segmento trancaria a base
 * inteira de e-mail/senha — os dois tenants de produção incluídos — para fora
 * da própria agenda. Quem tem tenant tem produto; falta de segmento degrada a
 * copy e vira BANNER no `AppLayout`, não cadeado. O porquê completo está em
 * `onboarding.js`.
 *
 * ⚠️ A guarda decide 100% no cliente e NUNCA dispara requisição. Não pode: o
 * interceptor de `services/api.js` responde a QUALQUER 401 com `clearSession()`
 * + `window.location.href = '/registration'`, sem allowlist. Um 401 vindo daqui
 * trocaria o beco sem saída por um laço de logout. É pelo mesmo motivo que o
 * backend responde 200-com-`tenant: null` no `GET /tenants/me` e 409
 * `TENANT_NOT_CONFIGURED` (não 401) no `POST /services`.
 *
 * Corolário: este módulo não importa `session.js` nem `api.js`. Encerrar sessão
 * continua sendo exclusividade de `clearSession()` (INV-F2), e a guarda não é
 * um caminho de logout.
 */
export default function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (!localStorage.getItem("token")) {
    return <Navigate to="/login" replace />;
  }
  if (loading) return null;
  if (location.pathname !== ONBOARDING_PATH && mustCompleteOnboarding(user)) {
    return <Navigate to={ONBOARDING_PATH} replace />;
  }
  return children;
}
